import React, { useState } from 'react';
import {
  SlidersHorizontal,
  Check,
  X,
  AlertTriangle,
  Info,
  Shield,
  Layers,
  Sparkles,
} from 'lucide-react';
import { useModules } from '../context/ModuleContext.tsx';
import { ModuleItem } from '../types/index.ts';

export const ModuleManagerPage: React.FC = () => {
  const { modulesList, toggleModule, loading } = useModules();
  const [warningModal, setWarningModal] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
  }>({
    isOpen: false,
    title: '',
    message: '',
  });
  const [filterCategory, setFilterCategory] = useState<string>('all');
  const [updatingCode, setUpdatingCode] = useState<string | null>(null);

  const handleToggle = async (mod: ModuleItem) => {
    setUpdatingCode(mod.code);
    const newStatus = !mod.isEnabled;
    const result = await toggleModule(mod.code, newStatus);
    setUpdatingCode(null);

    if (!result.success && result.message) {
      setWarningModal({
        isOpen: true,
        title: 'Module Dependency Restriction',
        message: result.message,
      });
    }
  };

  const categories = [
    { id: 'all', label: 'All Modules' },
    { id: 'operations', label: 'Operations & Engineering' },
    { id: 'procurement', label: 'Procurement & Materials' },
    { id: 'finance', label: 'Finance & Invoicing' },
    { id: 'people', label: 'People & Field Staff' },
    { id: 'system', label: 'System & Administration' },
  ];

  const filteredModules = modulesList.filter((m) => {
    if (filterCategory === 'all') return true;
    return m.category === filterCategory;
  });

  const activeCount = modulesList.filter((m) => m.isEnabled).length;
  const optionalInactiveCount = modulesList.filter((m) => !m.isEnabled).length;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-xl sm:text-2xl font-bold text-slate-900 tracking-tight">
              Module Manager
            </h1>
            <span className="px-2 py-0.5 rounded text-[11px] font-bold bg-blue-100 text-blue-800">
              Odoo-Style Architecture
            </span>
          </div>
          <p className="text-xs sm:text-sm text-slate-500 mt-0.5">
            Dynamically activate or deactivate ERP capabilities. Data is always preserved safely in PostgreSQL.
          </p>
        </div>

        <div className="flex items-center gap-3">
          <div className="px-3 py-1.5 bg-emerald-50 border border-emerald-200 rounded-lg text-xs font-semibold text-emerald-800">
            {activeCount} Active Modules
          </div>
          <div className="px-3 py-1.5 bg-slate-100 border border-slate-200 rounded-lg text-xs font-semibold text-slate-600">
            {optionalInactiveCount} Optional Inactive
          </div>
        </div>
      </div>

      {/* Dependency Info Banner */}
      <div className="p-4 bg-blue-50/80 border border-blue-200 rounded-xl flex items-start gap-3">
        <Info className="w-5 h-5 text-blue-700 shrink-0 mt-0.5" />
        <div className="text-xs text-blue-900 leading-relaxed">
          <span className="font-bold">Enterprise Dependency Engine:</span> Modules with strict operational dependencies (e.g.,{' '}
          <code className="bg-blue-100 px-1 py-0.5 rounded font-mono text-[11px]">Consolidated Invoices</code> requires{' '}
          <code className="bg-blue-100 px-1 py-0.5 rounded font-mono text-[11px]">Invoices</code> &amp;{' '}
          <code className="bg-blue-100 px-1 py-0.5 rounded font-mono text-[11px]">Accounts</code>) cannot be disabled without first resolving or disabling dependent workflows.
        </div>
      </div>

      {/* Category Tabs */}
      <div className="flex items-center gap-1.5 overflow-x-auto pb-1 border-b border-slate-200">
        {categories.map((c) => (
          <button
            key={c.id}
            onClick={() => setFilterCategory(c.id)}
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold whitespace-nowrap transition-colors ${
              filterCategory === c.id
                ? 'bg-[#0f2942] text-white shadow-2xs'
                : 'text-slate-600 hover:bg-slate-100'
            }`}
          >
            {c.label}
          </button>
        ))}
      </div>

      {/* Modules Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filteredModules.map((mod) => {
          const isBusy = updatingCode === mod.code;

          return (
            <div
              key={mod.code}
              className={`p-4 rounded-xl border transition-all ${
                mod.isEnabled
                  ? 'bg-white border-slate-200 shadow-2xs'
                  : 'bg-slate-50/70 border-slate-200 opacity-80'
              }`}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className="font-bold text-sm text-slate-900">{mod.name}</span>
                    {mod.isCore && (
                      <span className="text-[10px] font-bold px-1.5 py-0.2 rounded bg-slate-100 text-slate-700 border border-slate-200">
                        CORE
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-slate-500 mt-1 leading-snug">{mod.description}</p>
                </div>

                {/* Toggle switch */}
                <div>
                  <button
                    disabled={mod.isCore || isBusy}
                    onClick={() => handleToggle(mod)}
                    className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-hidden ${
                      mod.isCore
                        ? 'opacity-60 cursor-not-allowed bg-emerald-600'
                        : mod.isEnabled
                        ? 'bg-blue-700'
                        : 'bg-slate-300'
                    }`}
                  >
                    <span
                      className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow-lg ring-0 transition duration-200 ease-in-out ${
                        mod.isEnabled ? 'translate-x-5' : 'translate-x-0'
                      }`}
                    />
                  </button>
                </div>
              </div>

              {/* Dependencies badge if any */}
              {Array.isArray(mod.dependencies) && mod.dependencies.length > 0 && (
                <div className="mt-3 pt-3 border-t border-slate-100 flex items-center gap-1.5 text-[11px] text-slate-500">
                  <span className="font-semibold text-slate-700">Requires:</span>
                  <div className="flex items-center gap-1">
                    {mod.dependencies.map((d) => (
                      <span key={d} className="px-1.5 py-0.2 bg-slate-100 rounded text-[10px] font-mono text-slate-700">
                        {d}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Warning Modal for Dependency Violations */}
      {warningModal.isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-xs">
          <div className="bg-white rounded-xl shadow-2xl border border-slate-200 max-w-md w-full p-5 space-y-4 animate-in fade-in zoom-in-95">
            <div className="flex items-center gap-3 text-amber-700">
              <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center shrink-0">
                <AlertTriangle className="w-5 h-5" />
              </div>
              <h3 className="font-bold text-slate-900 text-base">{warningModal.title}</h3>
            </div>

            <p className="text-xs text-slate-600 leading-relaxed bg-amber-50 p-3 rounded-lg border border-amber-200">
              {warningModal.message}
            </p>

            <div className="flex justify-end pt-2">
              <button
                onClick={() => setWarningModal({ isOpen: false, title: '', message: '' })}
                className="px-4 py-2 bg-slate-800 hover:bg-slate-900 text-white rounded-lg text-xs font-semibold transition-colors"
              >
                Understood
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
